import globalMarketsRaw from './global_markets.json' with { type: 'json' };
import indiaStatesRaw from './india_states_uts.json' with { type: 'json' };
import indiaCitiesRaw from './india_city_seed.json' with { type: 'json' };
import tnDistrictsRaw from './tamil_nadu_districts.json' with { type: 'json' };
import serviceAudienceRaw from './service_audience_map.json' with { type: 'json' };
import { locationsData } from '../locationsData.js';

/**
 * Normalized Global Markets
 * Grouped into Tier A (high priority), Tier B (expansion), Tier C (research)
 */
const TIER_A_COUNTRIES = new Set([
  'United States', 'United Kingdom', 'United Arab Emirates', 'Singapore',
  'Australia', 'Canada', 'Germany', 'Kenya', 'Netherlands', 'Ireland',
  'New Zealand', 'Switzerland', 'Saudi Arabia', 'Qatar', 'Malaysia',
  'Japan', 'France', 'Sweden', 'Norway', 'Denmark'
]);

export const globalMarkets = globalMarketsRaw.map(m => {
  const name = m['Market Name'] || '';
  const isTierA = TIER_A_COUNTRIES.has(name);
  const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const published = Boolean(locationsData[id]);

  return {
    id,
    name,
    isoAlpha2: m['ISO Alpha-2'] || '',
    isoAlpha3: m['ISO Alpha-3'] || '',
    marketType: m['Market Type'] || 'Country',
    tier: isTierA ? 'Tier A' : (m['SEO Tier'] === 'Research' ? 'Tier C' : 'Tier B'),
    primaryLanguages: m['Primary Language(s)'] || 'English',
    targetIndustries: m['Target Industries'] || '',
    relevantServices: m['Relevant Byte Pairs Services'] || '',
    status: published ? 'published' : 'research',
    proposedUrl: published ? `/software-development-company-${id}` : '',
    notes: m['Notes'] || ''
  };
});

/**
 * Normalized India States & Union Territories
 */
export const indiaStates = indiaStatesRaw.map(s => {
  const name = s['State/UT'] || '';
  const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const published = Boolean(locationsData[id]);

  return {
    id,
    name,
    type: s['Type'] || 'State',
    priority: s['Priority'] || 'Standard',
    status: published ? 'published' : 'research',
    proposedUrl: published ? `/software-development-company-${id}` : '',
    inventoryStatus: s['City/Town Inventory Status'] || '',
    notes: s['Notes'] || ''
  };
});

/**
 * Normalized Tamil Nadu Districts (38 Districts)
 */
export const tamilNaduDistricts = tnDistrictsRaw.map(d => {
  const district = d['Tamil Nadu District'] || '';
  const representativeCity = d['Representative City / Market'] || district;
  const id = representativeCity.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const published = Boolean(locationsData[id]);

  return {
    id,
    district,
    representativeCity,
    targetAudience: d['Potential Industry Audience'] || '',
    servicesToValidate: d['Services to Validate'] || '',
    priority: d['SEO Priority'] || 'Standard',
    status: published ? 'published' : 'research',
    proposedUrl: published ? `/software-development-company-${id}` : '',
    notes: d['Notes'] || ''
  };
});

/**
 * Normalized India Cities (412 Seed Cities)
 */
export const indiaCities = indiaCitiesRaw.map(c => {
  const cityName = c['City/Town'] || '';
  const state = c['State/UT'] || '';
  const id = cityName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const published = Boolean(locationsData[id]);

  return {
    id,
    name: cityName,
    state,
    tier: c['Proposed SEO Tier'] || 'Tier 2',
    targetAudience: c['Potential Target Audience / Industry'] || '',
    servicesToValidate: c['Relevant Services to Validate'] || '',
    status: published ? 'published' : 'research',
    proposedUrl: published ? `/software-development-company-${id}` : '',
    notes: c['Notes'] || ''
  };
});

/**
 * Service to Audience & Keyword Map
 */
export const serviceAudienceMap = serviceAudienceRaw.map(s => ({
  service: s['Service'] || '',
  targetAudience: s['Target Audience'] || '',
  keywordCluster: s['Keyword Cluster'] || '',
  marketFit: s['Market Fit'] || '',
  existingProof: s['Existing Proof/Portfolio'] || '',
  pageStatus: s['Page Status'] || ''
}));

/**
 * Helper to retrieve currently published and verified location pages
 */
export const getPublishedLocations = () => {
  return Object.values(locationsData);
};

/**
 * Market lookup by unique ID
 */
export const getMarketById = (id) => {
  if (locationsData[id]) return { ...locationsData[id], published: true };
  const tn = tamilNaduDistricts.find(d => d.id === id);
  if (tn) return tn;
  const city = indiaCities.find(c => c.id === id);
  if (city) return city;
  const state = indiaStates.find(s => s.id === id);
  if (state) return state;
  const global = globalMarkets.find(g => g.id === id);
  if (global) return global;
  return null;
};
